import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import StatementPdf from "./statement-pdf";
import { buildStatementSnapshot, resolvePeriod } from "./build-statement-snapshot";
import { resolveMonthLabel } from "./statement-to-pdf-props";
import useStatementBuilderAccounts from "../../hooks/use-statement-builder-accounts";
import useStatementBuilderInvoices from "../../hooks/use-statement-builder-invoices";
import useSaveStatement from "../../hooks/use-save-statement";
import { compareAccountNames } from "../../util";
import valleyLogo from "../../assets/images/valley-irrigation-logo.png";

const COLUMN_CONFIG = [
  { key: "invoiceNumber", label: "Invoice #", fr: "1fr" },
  { key: "dealerName", label: "Dealer", fr: "1.5fr" },
  { key: "invoiceDate", label: "Date", fr: "0.9fr" },
  { key: "invoiceTotal", label: "Total", fr: "0.9fr" },
  { key: "rebateAmount", label: "Rebate", fr: "0.9fr" }
];

const INITIAL_COLUMNS = {
  invoiceNumber: true,
  dealerName: true,
  invoiceDate: true,
  invoiceTotal: true,
  rebateAmount: true
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const SECTION_LABEL = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#9ca3af",
  mb: 1.5
};

const MONTH_OPTIONS = (() => {
  const options = [];
  const year = new Date().getFullYear();
  for (let i = 0; i < 12; i++) {
    const d = new Date(year, i, 1);
    options.push({
      value: `${year}-${String(i + 1).padStart(2, "0")}`,
      label: d.toLocaleString("en-US", { month: "long" })
    });
  }
  return options;
})();

const TRANSITION_MS = 400;

const StatementBuilder = () => {
  const navigate = useNavigate();

  const { accounts, accountsLoading, accountsError, refetchAccounts } = useStatementBuilderAccounts();

  const [selectedFarms, setSelectedFarms] = useState([]);
  const [columns, setColumns] = useState(INITIAL_COLUMNS);
  const [transitioning, setTransitioning] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(true);
  const [accountSearch, setAccountSearch] = useState("");
  const [browseOpen, setBrowseOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[new Date().getMonth()].value);
  const [dateMode, setDateMode] = useState("month"); // "month" | "custom"
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [downloading, setDownloading] = useState(false);
  const timerRef = useRef(null);

  const deferUpdate = useCallback((applyFn) => {
    setTransitioning(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      applyFn();
      setTransitioning(false);
    }, TRANSITION_MS);
  }, []);

  // Clear any pending defer timer on unmount to avoid setState-on-unmounted
  // warnings and a leaked timer when navigating away quickly.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const monthLabel = useMemo(() => {
    const opt = MONTH_OPTIONS.find((o) => o.value === selectedMonth);
    return opt ? opt.label : MONTH_OPTIONS[0].label;
  }, [selectedMonth]);

  // All selectable farm/child ids from the fetched accounts.
  const allFarmIds = useMemo(
    () => accounts.flatMap((acct) => acct.farms.map((f) => f.id)),
    [accounts]
  );

  // If accounts reload, drop any selected ids that no longer exist to avoid
  // stale farm ids lingering in the selection.
  useEffect(() => {
    if (accountsLoading) return;
    setSelectedFarms((prev) => {
      const next = prev.filter((id) => allFarmIds.includes(id));
      return next.length === prev.length ? prev : next;
    });
  }, [allFarmIds, accountsLoading]);

  const toggleFarm = (farmId) => {
    deferUpdate(() => {
      setSelectedFarms((prev) => (
        prev.includes(farmId)
          ? prev.filter((id) => id !== farmId)
          : [...prev, farmId]
      ));
    });
  };

  const toggleParent = (account) => {
    const farmIds = account.farms.map((f) => f.id);
    const allSelected = farmIds.every((id) => selectedFarms.includes(id));
    deferUpdate(() => {
      setSelectedFarms((prev) =>
        allSelected
          ? prev.filter((id) => !farmIds.includes(id))
          : [...new Set([...prev, ...farmIds])]
      );
    });
  };

  // Resolve the active period (YYYY-MM-DD) for the invoice query.
  const period = useMemo(
    () => resolvePeriod({ dateMode, selectedMonth, customStart, customEnd }),
    [dateMode, selectedMonth, customStart, customEnd]
  );

  // Period-aware label for the live preview: reflects custom date ranges, with
  // a fallback to the month label while custom dates are incomplete.
  const previewLabel = useMemo(
    () => resolveMonthLabel(period) || monthLabel,
    [period, monthLabel]
  );

  const {
    invoices: fetchedInvoices,
    invoicesLoading,
    invoicesError,
    refetchInvoices
  } = useStatementBuilderInvoices({
    start: period.period_start,
    end: period.period_end,
    farmIds: selectedFarms
  });

  // Only show invoices for currently-selected farms (guards against stale rows
  // from a previous selection while a refetch is in flight).
  const visibleInvoices = useMemo(
    () => fetchedInvoices.filter((inv) => selectedFarms.includes(inv.farmId)),
    [fetchedInvoices, selectedFarms]
  );

  const { mutateAsync: saveStatement, error: saveError } = useSaveStatement();

  const selectedInvoiceCount = visibleInvoices.length;

  // Filtered accounts for browse/search
  const filteredAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    const base = !q
      ? accounts
      : accounts.filter((acct) =>
          acct.name.toLowerCase().includes(q) ||
          acct.farms.some((f) => f.name.toLowerCase().includes(q))
        );

    // Sort A–Z by parent name (letters before numbers/symbols).
    return [...base].sort((a, b) => compareAccountNames(a.name, b.name));
  }, [accountSearch, accounts]);

  const toggleColumn = (key) => {
    deferUpdate(() => {
      setColumns((prev) => ({ ...prev, [key]: !prev[key] }));
    });
  };

  const visibleColumns = COLUMN_CONFIG.filter((col) => columns[col.key]);
  const grandTotal = visibleInvoices.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0);
  const grandRebate = visibleInvoices.reduce((s, inv) => s + (inv.rebateAmount || 0), 0);

  const handleDownload = async () => {
    if (visibleInvoices.length === 0 || downloading) return;
    setDownloading(true);
    try {
      // Build the save payload from the same values the preview shows.
      const snapshot = buildStatementSnapshot({
        dateMode,
        selectedMonth,
        customStart,
        customEnd,
        columns,
        visibleInvoices,
        grandTotal,
        grandRebate
      });

      // Persist first — if the save fails this throws and we skip the download.
      const saved = await saveStatement(snapshot);
      const reference = saved?.reference;

      // The save is the source of truth. Treat the PDF/download as best-effort:
      // if it fails after a successful save we still navigate to the list (where
      // the statement can be re-downloaded) rather than stranding the user on the
      // builder, where a retry would create a duplicate statement.
      try {
        // Derive the period label from the saved snapshot so custom date ranges
        // are reflected accurately (same logic as the statements download path).
        const periodLabel = resolveMonthLabel(snapshot);

        const doc = (
          <StatementPdf
            monthLabel={periodLabel}
            visibleColumns={visibleColumns}
            visibleInvoices={visibleInvoices}
            grandTotal={grandTotal}
            grandRebate={grandRebate}
            reference={reference}
          />
        );
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileLabel = (reference || periodLabel.replace(/\s+/g, "-")).toLowerCase();
        a.download = `statement-${fileLabel}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (pdfErr) {
        console.error("Statement saved, but PDF download failed", pdfErr);
      }

      navigate("/statements");
    } catch (err) {
      console.error("Failed to save statement", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    /* Full-height two-zone layout — no outer card wrapper */
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 64px)" }}>

      {/* Top bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 4,
          py: 2,
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "#fff",
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "2px",
            width: "100%",
            background: "linear-gradient(90deg, transparent 0%, rgba(99,102,241,0.45) 40%, rgba(99,102,241,0.15) 100%)",
            transform: transitioning ? "translateX(0%)" : "translateX(-101%)",
            transition: transitioning
              ? `transform ${TRANSITION_MS}ms ease-out`
              : "none",
          }
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, letterSpacing: -0.3 }}>
            Build Statement
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af", mt: 0.25 }}>
            {previewLabel}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {saveError && (
            <Typography sx={{ fontSize: "0.8rem", color: "#ef4444", fontWeight: 600 }}>
              Couldn't save. Try again.
            </Typography>
          )}
          <Button
            onClick={() => setShowPreview((v) => !v)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}
          >
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button>
          <Button
            onClick={() => navigate("/statements")}
            sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleDownload}
            disabled={visibleInvoices.length === 0 || downloading}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 2.5 }}
          >
            {downloading ? "Saving…" : "Save & Download"}
          </Button>
        </Stack>
      </Stack>

      {/* Two-zone body */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Left — controls */}
        <Box
          sx={{
            width: 480,
            flexShrink: 0,
            borderRight: "1px solid #e5e7eb",
            bgcolor: "#fff",
            px: 5,
            py: 4,
            overflowY: "auto"
          }}
        >
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, mb: 0.5 }}>
            Document Options
          </Typography>
          <Typography variant="body2" sx={{ color: "#9ca3af", mb: 3 }}>
            Choose what to include in your statement.
          </Typography>

          {/* Invoice Date */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...SECTION_LABEL, mb: 1.5 }}>Invoice Date</Typography>

            {/* Segmented toggle */}
            <Stack direction="row" spacing={0} sx={{ mb: 2, bgcolor: "#f3f4f6", borderRadius: 2, p: 0.4 }}>
              <Box
                onClick={() => {
                  setDateMode("month");
                  // Pre-fill custom from selected month when switching back
                }}
                sx={{
                  flex: 1,
                  py: 0.75,
                  textAlign: "center",
                  borderRadius: 1.5,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  userSelect: "none",
                  bgcolor: dateMode === "month" ? "#fff" : "transparent",
                  color: dateMode === "month" ? "#1f2937" : "#6b7280",
                  boxShadow: dateMode === "month" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  transition: "all 150ms ease"
                }}
              >
                Month
              </Box>
              <Box
                onClick={() => {
                  setDateMode("custom");
                  // Pre-fill custom dates from selected month
                  const [y, m] = selectedMonth.split("-");
                  const start = `${y}-${m}-01`;
                  const end = new Date(Number(y), Number(m), 0);
                  setCustomStart(start);
                  setCustomEnd(`${y}-${m}-${String(end.getDate()).padStart(2, "0")}`);
                }}
                sx={{
                  flex: 1,
                  py: 0.75,
                  textAlign: "center",
                  borderRadius: 1.5,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  userSelect: "none",
                  bgcolor: dateMode === "custom" ? "#fff" : "transparent",
                  color: dateMode === "custom" ? "#1f2937" : "#6b7280",
                  boxShadow: dateMode === "custom" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  transition: "all 150ms ease"
                }}
              >
                Custom
              </Box>
            </Stack>

            {/* Month mode */}
            {dateMode === "month" && (
              <Select
                value={selectedMonth}
                onChange={(e) => {
                  deferUpdate(() => setSelectedMonth(e.target.value));
                }}
                size="small"
                fullWidth
                sx={{ borderRadius: 1.5, fontSize: "0.875rem" }}
              >
                {MONTH_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* Custom mode */}
            {dateMode === "custom" && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <TextField
                  type="date"
                  value={customStart}
                  onChange={(e) => {
                    setCustomStart(e.target.value);
                    deferUpdate(() => {});
                  }}
                  size="small"
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: "0.82rem" },
                 }}
                />
                <ArrowForwardIcon sx={{ fontSize: 16, color: "#9ca3af", flexShrink: 0 }} />
                <TextField
                  type="date"
                  value={customEnd}
                  onChange={(e) => {
                    setCustomEnd(e.target.value);
                    deferUpdate(() => {});
                  }}
                  size="small"
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: "0.82rem" } }}
                />
              </Stack>
            )}
          </Box>

          {/* Columns */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => setFieldsOpen((v) => !v)}
              sx={{ cursor: "pointer", mb: fieldsOpen ? 1.5 : 0, userSelect: "none", py: 1, px: 1.5, borderRadius: 1.5, "&:hover": { bgcolor: "#f9fafb" } }}
            >
              <Typography sx={{ ...SECTION_LABEL, mb: 0 }}>Columns</Typography>
              <Typography sx={{ fontSize: "1.1rem", color: "#6b7280", lineHeight: 1, transition: "transform 200ms", transform: fieldsOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
                ▾
              </Typography>
            </Stack>
            {fieldsOpen && (
              <Box>
                <Typography variant="caption" sx={{ color: "#9ca3af", display: "block", mb: 1.5 }}>
                  Tap to include or exclude columns from the statement.
                </Typography>
                {/* Table header strip */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "#f9fafb"
                  }}
                >
                  {COLUMN_CONFIG.map((col, i) => {
                    const active = columns[col.key];
                    return (
                      <Box
                        key={col.key}
                        onClick={() => toggleColumn(col.key)}
                        sx={{
                          flex: 1,
                          py: 1.25,
                          px: 0.5,
                          textAlign: "center",
                          cursor: "pointer",
                          userSelect: "none",
                          position: "relative",
                          borderRight: i < COLUMN_CONFIG.length - 1 ? "1px solid #e5e7eb" : "none",
                          bgcolor: active ? "#fff" : "transparent",
                          transition: "all 180ms ease",
                          "&:hover": {
                            bgcolor: active ? "#fff" : "#f3f4f6"
                          },
                          "&::after": active ? {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: "20%",
                            right: "20%",
                            height: "2px",
                            bgcolor: "#6366f1",
                            borderRadius: "1px"
                          } : {}
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: active ? 700 : 500,
                            color: active ? "#1f2937" : "#d1d5db",
                            letterSpacing: "0.01em",
                            transition: "all 180ms ease",
                            textDecoration: active ? "none" : "line-through",
                            textDecorationColor: "#e5e7eb"
                          }}
                        >
                          {col.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Accounts Included */}
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => setAccountsOpen((v) => !v)}
              sx={{ cursor: "pointer", mb: accountsOpen ? 1.5 : 0, userSelect: "none", py: 1, px: 1.5, borderRadius: 1.5, "&:hover": { bgcolor: "#f9fafb" } }}
            >
              <Typography sx={{ ...SECTION_LABEL, mb: 0 }}>Accounts Included</Typography>
              <Typography sx={{ fontSize: "1.1rem", color: "#6b7280", lineHeight: 1, transition: "transform 200ms", transform: accountsOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
                ▾
              </Typography>
            </Stack>
            {accountsOpen && (
              <Box>
                {/* Selection summary */}
                <Typography sx={{ fontSize: "0.8rem", color: "#6b7280", mb: 2 }}>
                  {selectedInvoiceCount === 0
                    ? "No invoices selected"
                    : `${selectedInvoiceCount} invoice${selectedInvoiceCount !== 1 ? "s" : ""} selected`}
                </Typography>

                {accountsLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
                    <CircularProgress size={16} sx={{ color: "#6366f1" }} />
                    <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                      Loading accounts…
                    </Typography>
                  </Stack>
                ) : accountsError ? (
                  <Stack spacing={1} sx={{ py: 2 }}>
                    <Typography sx={{ fontSize: "0.8rem", color: "#b91c1c" }}>
                      Couldn't load accounts.
                    </Typography>
                    <Typography
                      onClick={() => refetchAccounts()}
                      sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#6366f1", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      Try again
                    </Typography>
                  </Stack>
                ) : accounts.length === 0 ? (
                  <Typography sx={{ fontSize: "0.8rem", color: "#9ca3af", py: 2 }}>
                    No accounts available.
                  </Typography>
                ) : (
                <>
                {/* Search */}
                <TextField
                  placeholder="Search accounts or farms..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      fontSize: "0.82rem",
                      bgcolor: "#f9fafb",
                      "& fieldset": { borderColor: "#e5e7eb" },
                      "&:hover fieldset": { borderColor: "#d1d5db" },
                      "&.Mui-focused fieldset": { borderColor: "#6366f1" }
                    }
                  }}
                />

                {/* Browse all / Search results */}
                <Box>
                  {!accountSearch && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: browseOpen ? 1.5 : 0, py: 0.5, userSelect: "none" }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        onClick={() => setBrowseOpen((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#6366f1" }}>
                          {browseOpen ? "Hide all accounts" : "Browse all accounts"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "#6366f1", lineHeight: 1, transition: "transform 200ms", transform: browseOpen ? "rotate(0deg)" : "rotate(-90deg)" }}>
                          ▾
                        </Typography>
                      </Stack>
                      {browseOpen && (
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <Typography
                            onClick={() => deferUpdate(() => setSelectedFarms(allFarmIds))}
                            sx={{ fontSize: "0.80rem", fontWeight: 600, color: "#6366f1", cursor: "pointer", px: 0.5, py: 0.25, borderRadius: 1, "&:hover": { bgcolor: "#eef2ff" } }}
                          >
                            All
                          </Typography>
                          <Typography sx={{ fontSize: "0.65rem", color: "#6366f1" }}>·</Typography>
                          <Typography
                            onClick={() => deferUpdate(() => setSelectedFarms([]))}
                            sx={{ fontSize: "0.80rem", fontWeight: 600, color: "#6b7280", cursor: "pointer", px: 0.5, py: 0.25, borderRadius: 1, "&:hover": { bgcolor: "#f3f4f6" } }}
                          >
                            None
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  )}

                  {(browseOpen || accountSearch) && (
                    <Box sx={{ maxHeight: 440, overflowY: "auto", pr: 0.5 }}>
                      {filteredAccounts.length === 0 ? (
                        <Typography sx={{ fontSize: "0.78rem", color: "#9ca3af", py: 1 }}>
                          No accounts match "{accountSearch}"
                        </Typography>
                      ) : filteredAccounts.map((acct) => {
                        const farmIds = acct.farms.map((f) => f.id);
                        const allSelected = farmIds.length > 0 && farmIds.every((id) => selectedFarms.includes(id));
                        const someSelected = farmIds.some((id) => selectedFarms.includes(id));
                        return (
                          <Box key={acct.id} sx={{ mb: 2.5 }}>
                            {/* Parent row */}
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.75}
                              onClick={() => toggleParent(acct)}
                              sx={{
                                py: 0.5,
                                px: 0.75,
                                cursor: "pointer",
                                borderRadius: 1.5,
                                "&:hover": { bgcolor: "#f9fafb" }
                              }}
                            >
                              <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected && !allSelected}
                                size="small"
                                sx={{
                                  p: 0,
                                  "& .MuiSvgIcon-root": { fontSize: 16 },
                                  color: "#d1d5db",
                                  "&.Mui-checked": { color: "#6366f1" },
                                  "&.MuiCheckbox-indeterminate": { color: "#6366f1" }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleParent(acct)}
                              />
                              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#1f2937" }}>
                                {acct.name}
                              </Typography>
                              <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af" }}>
                                {acct.farms.length} farm{acct.farms.length !== 1 ? "s" : ""}
                              </Typography>
                            </Stack>
                            {/* Child farms */}
                            {acct.farms.map((farm) => {
                              const isSelected = selectedFarms.includes(farm.id);
                              return (
                                <Stack
                                  key={farm.id}
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.75}
                                  onClick={() => toggleFarm(farm.id)}
                                  sx={{
                                    pl: 3.5,
                                    py: 0.35,
                                    cursor: "pointer",
                                    borderRadius: 1,
                                    "&:hover": { bgcolor: "#f9fafb" }
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    size="small"
                                    sx={{
                                      p: 0,
                                      "& .MuiSvgIcon-root": { fontSize: 14 },
                                      color: "#d1d5db",
                                      "&.Mui-checked": { color: "#6366f1" }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => toggleFarm(farm.id)}
                                  />
                                  <Typography sx={{ fontSize: "0.72rem", color: isSelected ? "#1f2937" : "#6b7280" }}>
                                    {farm.name}
                                  </Typography>
                                </Stack>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right — preview panel */}
        {showPreview && (
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            bgcolor: "#f5f5f5",
            px: 5,
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 720 }}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, mb: 0.5 }}>
              Preview
            </Typography>
            <Typography variant="body2" sx={{ color: "#9ca3af", mb: 3 }}>
              Live preview of your statement PDF.
            </Typography>
          </Box>

          {/* The document — US Letter proportions, scaled to fit */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 720,
              minHeight: 820,
              flexShrink: 0,
              border: "1px solid #e5e7eb",
              borderRadius: 3,
              boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
              p: "48px 56px",
              bgcolor: "#fff"
            }}
          >
            {/* Document header */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Box component="img" src={valleyLogo} alt="Valley Irrigation" sx={{ height: 28, width: "auto" }} />
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.3 }}>Generated</Typography>
                <Typography sx={{ fontSize: "0.78rem", color: "#374151" }}>{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</Typography>
              </Box>
            </Stack>

            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, letterSpacing: -0.5, mb: 0.25 }}>
                Rebate Statement
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>Selected period: {previewLabel}</Typography>
            </Box>

            {/* Grand totals bar */}
            <Stack alignItems="flex-end" sx={{ py: 1.5, borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", mb: 3 }}>
              <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", minWidth: 120, textAlign: "right" }}>Total Invoice Amount</Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, minWidth: 90, textAlign: "right" }}>{formatCurrency(grandTotal)}</Typography>
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", minWidth: 120, textAlign: "right" }}>Total Rebate Amount</Typography>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#10b981", minWidth: 90, textAlign: "right" }}>{formatCurrency(grandRebate)}</Typography>
              </Stack>
            </Stack>

            {/* Table — Grouped preview */}
            <Box>
              {invoicesLoading ? (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 3 }}>
                  <CircularProgress size={16} sx={{ color: "#6366f1" }} />
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    Loading invoices…
                  </Typography>
                </Stack>
              ) : invoicesError ? (
                <Stack spacing={1} sx={{ py: 3 }}>
                  <Typography variant="body2" sx={{ color: "#b91c1c" }}>
                    Couldn't load invoices.
                  </Typography>
                  <Typography
                    onClick={() => refetchInvoices()}
                    sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#6366f1", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  >
                    Try again
                  </Typography>
                </Stack>
              ) : visibleInvoices.length === 0 ? (
                <Typography variant="body2" sx={{ py: 3, color: "#9ca3af" }}>
                  {selectedFarms.length === 0
                    ? "Select one or more farms to preview their invoices."
                    : "No invoices for the selected farms and period."}
                </Typography>
              ) : (() => {
                // Group invoices by parent → child for preview
                const grouped = {};
                visibleInvoices.forEach((inv) => {
                  const parent = inv.parentAccount || inv.farmName;
                  const child = inv.farmName;
                  if (!grouped[parent]) grouped[parent] = {};
                  if (!grouped[parent][child]) grouped[parent][child] = [];
                  grouped[parent][child].push(inv);
                });

                // Sort parents A–Z so the summary, detail, and downloaded PDF
                // all match the sorted account picker.
                const sortedParents = Object.entries(grouped).sort(([a], [b]) =>
                  compareAccountNames(a, b)
                );

                return (
                  <>
                    {/* Account Summary */}
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1f2937", mb: 1 }}>Account Summary</Typography>
                    <Box sx={{ bgcolor: "#f9fafb", borderRadius: 1, py: 0.75, px: 1.5, mb: 0.5, display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 1.2fr 1.2fr", columnGap: 1 }}>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Account</Typography>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Rewards ID</Typography>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Farms</Typography>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Invoice </Typography>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Invoice Total</Typography>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Rebate</Typography>
                    </Box>
                    {sortedParents.map(([parentName, children]) => {
                      const childEntries = Object.entries(children);
                      const invCount = childEntries.reduce((s, [, invs]) => s + invs.length, 0);
                      const invTotal = childEntries.reduce((s, [, invs]) => s + invs.reduce((t, inv) => t + (inv.invoiceTotal || 0), 0), 0);
                      const rebTotal = childEntries.reduce((s, [, invs]) => s + invs.reduce((t, inv) => t + (inv.rebateAmount || 0), 0), 0);
                      const parentReward = childEntries.flatMap(([, invs]) => invs).find((inv) => inv.parentRewardsCode)?.parentRewardsCode || null;
                      return (
                        <Box key={parentName} sx={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 0.8fr 1.2fr 1.2fr", columnGap: 1, py: 0.75, px: 1.5, borderBottom: "1px solid #f3f4f6" }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{parentName}</Typography>
                          <Typography sx={{ fontSize: "0.72rem", fontFamily: "monospace", color: parentReward ? "#6b7280" : "#d1d5db" }}>{parentReward || "\u2014"}</Typography>
                          <Typography sx={{ fontSize: "0.78rem" }}>{childEntries.length}</Typography>
                          <Typography sx={{ fontSize: "0.78rem" }}>{invCount}</Typography>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>{formatCurrency(invTotal)}</Typography>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#10b981" }}>{formatCurrency(rebTotal)}</Typography>
                        </Box>
                      );
                    })}

                    {/* Detail by Account */}
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#1f2937", mt: 5, mb: 1 }}>Detail by Account</Typography>
                    {sortedParents.map(([parentName, children]) => {
                      const allInvs = Object.values(children).flat();
                      const parentInvTotal = allInvs.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0);
                      const parentRebTotal = allInvs.reduce((s, inv) => s + (inv.rebateAmount || 0), 0);
                      const parentReward = allInvs.find((inv) => inv.parentRewardsCode)?.parentRewardsCode || null;
                      return (
                        <Box key={parentName} sx={{ mb: 2 }}>
                          {/* Parent header */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ bgcolor: "#f3f4f6", borderRadius: 1, py: 0.75, px: 1.5, mb: 0.5 }}>
                            <Stack spacing={0.15}>
                              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{parentName}</Typography>
                              {parentReward && (
                                <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                  <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.04em" }}>REWARDS ID</Typography>
                                  <Typography sx={{ fontSize: "0.68rem", fontFamily: "monospace", color: "#6b7280" }}>{parentReward}</Typography>
                                </Stack>
                              )}
                            </Stack>
                            <Stack alignItems="flex-end">
                              <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.15 }}>
                                <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af" }}>Invoice Total</Typography>
                                <Typography sx={{ fontSize: "0.65rem", color: "#374151" }}>{formatCurrency(parentInvTotal)}</Typography>
                              </Stack>
                              <Stack direction="row" alignItems="baseline" spacing={0.75}>
                                <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af" }}>Rebate</Typography>
                                <Typography sx={{ fontSize: "0.65rem", color: "#10b981" }}>{formatCurrency(parentRebTotal)}</Typography>
                              </Stack>
                            </Stack>
                          </Stack>

                          {/* Child farms */}
                          {Object.entries(children).map(([childName, invoices]) => {
                            const childInvTotal = invoices.reduce((s, inv) => s + (inv.invoiceTotal || 0), 0);
                            const childRebTotal = invoices.reduce((s, inv) => s + (inv.rebateAmount || 0), 0);
                            const childReward = invoices.find((inv) => inv.farmRewardsCode)?.farmRewardsCode || null;
                            return (
                              <Box key={childName} sx={{ pl: 2, mb: 1 }}>
                                <Box sx={{ mb: 0.5 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#374151" }}>{childName}</Typography>
                                    <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af" }}>{invoices.length} invoice{invoices.length !== 1 ? "s" : ""}</Typography>
                                  </Stack>
                                  {childReward && (
                                    <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                      <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.04em" }}>REWARDS ID</Typography>
                                      <Typography sx={{ fontSize: "0.66rem", fontFamily: "monospace", color: "#6b7280" }}>{childReward}</Typography>
                                    </Stack>
                                  )}
                                </Box>

                                {/* Invoice header */}
                                <Box sx={{ display: "grid", gridTemplateColumns: visibleColumns.map((col) => col.key === "dealerName" ? "1.5fr" : "1fr").join(" "), columnGap: 1, pl: 1, mb: 0.25 }}>
                                  {visibleColumns.map((col) => (
                                    <Typography key={col.key} sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>{col.label}</Typography>
                                  ))}
                                </Box>

                                {/* Invoice rows */}
                                {invoices.map((inv) => (
                                  <Box key={inv.id} sx={{ display: "grid", gridTemplateColumns: visibleColumns.map((col) => col.key === "dealerName" ? "1.5fr" : "1fr").join(" "), columnGap: 1, pl: 1, py: 0.5, borderBottom: "1px solid #f9fafb" }}>
                                    {visibleColumns.map((col) => (
                                      <Typography
                                        key={col.key}
                                        sx={{
                                          fontSize: "0.72rem",
                                          fontWeight: col.key === "invoiceTotal" || col.key === "rebateAmount" ? 600 : 400,
                                          color: col.key === "rebateAmount" ? "#10b981" : "inherit"
                                        }}
                                      >
                                        {col.key === "invoiceTotal" || col.key === "rebateAmount"
                                          ? formatCurrency(inv[col.key])
                                          : col.key === "invoiceDate"
                                            ? formatDate(inv[col.key])
                                            : inv[col.key]}
                                      </Typography>
                                    ))}
                                  </Box>
                                ))}

                                {/* Child subtotal */}
                                <Stack alignItems="flex-end" sx={{ pr: 0.5, pt: 1, pb: 1.5 }}>
                                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.25 }}>
                                    <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af", minWidth: 50, textAlign: "right" }}>Subtotal:</Typography>
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, minWidth: 65, textAlign: "right" }}>{formatCurrency(childInvTotal)}</Typography>
                                  </Stack>
                                  <Stack direction="row" alignItems="baseline" spacing={1}>
                                    <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af", minWidth: 50, textAlign: "right" }}>Rebate:</Typography>
                                    <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#10b981", minWidth: 65, textAlign: "right" }}>{formatCurrency(childRebTotal)}</Typography>
                                  </Stack>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })}

                    {/* Footer totals */}
                    <Stack alignItems="flex-end" sx={{ pt: 2, mt: 1, borderTop: "2px solid #e5e7eb" }}>
                      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", minWidth: 120, textAlign: "right" }}>Grand Invoice Total</Typography>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, minWidth: 90, textAlign: "right" }}>{formatCurrency(grandTotal)}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="baseline" spacing={1.5}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", minWidth: 120, textAlign: "right" }}>Grand Rebate Total</Typography>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#10b981", minWidth: 90, textAlign: "right" }}>{formatCurrency(grandRebate)}</Typography>
                      </Stack>
                    </Stack>

                    {/* Copyright */}
                    <Box sx={{ mt: 4, pt: 1.5, borderTop: "0.5px solid #e5e7eb", textAlign: "center" }}>
                      <Typography sx={{ fontSize: "0.6rem", color: "#9ca3af" }}>© 2026 Valmont Industries, Inc.</Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Paper>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default StatementBuilder;
